import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Award, BookOpen, CheckCircle2, ChevronDown, Circle, CircleDot, Clock, Lock, LogIn, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { studentsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Avatar, Badge, EmptyPanel, StatCards, adminCard, fmtDate } from '../../components/adminUi';
import { ProgressBar, stateBadge } from './CourseStudents';

// Course editor · Students tab → one student (s155, admin only): summary,
// curriculum lesson-by-lesson (completed / in progress / not started / not yet
// released), evaluation attempts and a recent-activity timeline.
// "Back to students" returns to the course with the Students tab open.

const LESSON = {
  completed: { icon: CheckCircle2, cls: 'text-lms-free', label: 'Completed' },
  in_progress: { icon: CircleDot, cls: 'text-lms-link', label: 'In progress' },
  not_started: { icon: Circle, cls: 'text-lms-faint', label: 'Not started' },
  locked: { icon: Lock, cls: 'text-lms-faint', label: 'Not released yet' },
};

const EVENT = {
  enrolled: LogIn, opened: BookOpen, completed: CheckCircle2, passed: Award, attempt: XCircle,
};

function eventText(ev) {
  const q = (x) => `“${x || t('a lesson')}”`;
  const what = ev.eval === 'quiz' ? t('the quiz of') : t('the comprehension check of');
  switch (ev.kind) {
    case 'enrolled': return `${t('Enrolled in')} ${q(ev.title)}`;
    case 'opened': return `${t('Opened')} ${q(ev.title)}`;
    case 'completed': return `${t('Completed')} ${q(ev.title)}`;
    case 'passed': return `${t('Passed')} ${what} ${q(ev.title)} · ${ev.score}/${ev.total}`;
    default: return `${t('Attempted')} ${what} ${q(ev.title)} · ${ev.score}/${ev.total}`;
  }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const h2 = 'text-[17px] font-bold text-lms-ink';

// Question-by-question review: the student's pick vs the right option(s).
function AnswerList({ answers, className = '' }) {
  return (
    <ol className={`flex flex-col gap-4 ${className}`}>
      {answers.map((a, i) => (
        <li key={i} className="flex flex-col gap-2">
          <div className="flex items-start gap-2 text-[13.5px] font-semibold text-lms-ink">
            {a.ok
              ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-lms-free" aria-label={t('Correct')} />
              : <XCircle size={16} className="mt-0.5 shrink-0 text-lms-sale" aria-label={t('Incorrect')} />}
            <span className="min-w-0"><span className="lms-num">{i + 1}.</span> {a.question}</span>
          </div>
          {a.picked.length === 0 && <p className="pl-6 text-xs italic text-lms-sub">{t('Not answered')}</p>}
          <ul className="flex flex-col gap-1 pl-6">
            {a.options.map((opt, j) => {
              const picked = a.picked.includes(j);
              const right = a.correct.includes(j);
              if (!picked && !right) return <li key={j} className="text-[13px] text-lms-sub">{opt}</li>;
              return (
                <li
                  key={j}
                  className={`flex flex-wrap items-center gap-x-2 rounded-md px-2 py-1 text-[13px] ${
                    right ? 'bg-lms-tag-free-bg text-lms-tag-free-ink' : 'bg-[color-mix(in_srgb,var(--lms-sale)_12%,transparent)] text-lms-sale'
                  }`}
                >
                  <span className="min-w-0 font-semibold">{opt}</span>
                  <span className="text-[11.5px] font-bold uppercase tracking-[0.03em]">
                    {picked && right ? t('Their answer · correct') : picked ? t('Their answer') : t('Correct answer')}
                  </span>
                </li>
              );
            })}
          </ul>
          {a.explanation && <p className="pl-6 text-xs text-lms-sub">{t('Why')}: {a.explanation}</p>}
        </li>
      ))}
    </ol>
  );
}

// One evaluation attempt; expands to the question-by-question review
// (student's pick vs the right option).
function EvalRow({ ev }) {
  const [open, setOpen] = useState(false);
  const answers = ev.answers || [];
  return (
    <li className="border-b border-lms-line last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        disabled={!answers.length}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left enabled:hover:bg-lms-raised"
      >
        <ChevronDown size={16} className={`mt-0.5 shrink-0 text-lms-sub transition-transform duration-200 ${open ? '' : '-rotate-90'} ${answers.length ? '' : 'invisible'}`} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-lms-ink">{ev.lesson_title || '—'}</div>
          <div className="text-xs text-lms-sub">
            {ev.kind === 'quiz' ? t('Quiz') : t('Comprehension check')} · {fmtDateTime(ev.taken_at)}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-sm font-bold text-lms-ink lms-num">{ev.score}/{ev.total}</span>
          <Badge kind={ev.passed ? 'ok' : 'bad'}>{ev.passed ? t('Approved') : t('Not passed')}</Badge>
        </div>
      </button>
      {open && <AnswerList answers={answers} className="px-4 pb-4 pt-1" />}
    </li>
  );
}

function Module({ m, defaultOpen, evaluations }) {
  const [shown, setShown] = useState(null); // lesson id whose answers are open
  const [open, setOpen] = useState(defaultOpen);
  const pct = m.total ? Math.round((100 * m.done) / m.total) : 0;
  return (
    <div className="border-b border-lms-line last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-lms-raised sm:px-5"
      >
        <ChevronDown size={16} className={`shrink-0 text-lms-sub transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-lms-ink">{m.title}</span>
        <span className="shrink-0 text-[13px] text-lms-sub lms-num">{m.done}/{m.total}</span>
        {pct === 100 && <Badge kind="ok">{t('Done')}</Badge>}
      </button>
      {open && (
        <ul className="flex flex-col pb-2">
          {m.lessons.map((l) => {
            const meta = LESSON[l.status] || LESSON.not_started;
            const Icon = meta.icon;
            const ev = l.quiz || l.check;
            const latest = evaluations.find((x) => x.lesson_id === l.id); // newest first
            return (
              <li key={l.id} className="flex gap-3 px-4 py-2.5 sm:px-5 sm:pl-12">
                <Icon size={18} className={`mt-0.5 shrink-0 ${meta.cls}`} aria-label={t(meta.label)} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-lms-ink">{l.title}</div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-lms-sub">
                    <span>{t(meta.label)}</span>
                    {l.status === 'completed' && (
                      <span>{l.completed_at ? `${t('on')} ${fmtDateTime(l.completed_at)}` : t('date not recorded')}</span>
                    )}
                    {l.first_opened_at && <span>{t('First opened')}: {fmtDate(l.first_opened_at)}</span>}
                    {l.views > 0 && <span className="lms-num">{l.views} {t(l.views === 1 ? 'visit' : 'visits')}</span>}
                    {ev && (
                      <span className={`font-semibold lms-num ${ev.passed ? 'text-lms-free' : 'text-lms-sale'}`}>
                        {l.quiz ? t('Quiz') : t('Check')}: {ev.best_score}/{ev.total} · {ev.attempts} {t(ev.attempts === 1 ? 'attempt' : 'attempts')}
                      </span>
                    )}
                  </div>
                  {latest && latest.answers.length > 0 && (
                    <button
                      type="button"
                      aria-expanded={shown === l.id}
                      onClick={() => setShown(shown === l.id ? null : l.id)}
                      className="mt-1.5 inline-flex h-8 items-center gap-1 rounded-md text-xs font-bold text-lms-link hover:underline"
                    >
                      <ChevronDown size={14} className={`transition-transform duration-200 ${shown === l.id ? '' : '-rotate-90'}`} />
                      {shown === l.id ? t('Hide answers') : t('View answers')}
                    </button>
                  )}
                  {shown === l.id && latest && (
                    <div className="mt-2 rounded-lg border border-lms-line bg-lms-raised">
                      <p className="px-3 pt-3 text-xs text-lms-sub">
                        {t('Latest attempt')} · {fmtDateTime(latest.taken_at)} · <span className="font-bold text-lms-ink lms-num">{latest.score}/{latest.total}</span>
                      </p>
                      <AnswerList answers={latest.answers} className="p-3" />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function CourseStudentDetail() {
  const { courseId, memberId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    studentsAPI.get(courseId, memberId)
      .then(({ data }) => setD(data))
      .catch((e) => {
        setErr(e.response?.data?.detail || t('Could not load this student'));
        toast.error(t('Could not load this student'));
      });
  }, [courseId, memberId]);

  const back = (
    <Link
      to={`/admin/courses/${courseId}?tab=students`}
      className="lms-press inline-flex h-11 items-center gap-2 self-start rounded-[10px] border border-lms-line bg-lms-surface px-4 text-sm font-bold text-lms-ink hover:border-lms-ink"
    >
      <ArrowLeft size={16} /> {t('Back to students')}
    </Link>
  );

  if (err) return <div className="flex flex-col gap-5">{back}<EmptyPanel title={err} /></div>;
  if (!d) return <div className="flex flex-col gap-5">{back}<p className="text-sm text-lms-muted">{t('Loading…')}</p></div>;

  const { member, course, summary: s, enrollment: e } = d;
  const firstOpen = Math.max(0, d.modules.findIndex((m) => m.done < m.total));

  return (
    <div className="flex flex-col gap-6">
      {back}

      {/* ===== HEADER ===== */}
      <div className={`${adminCard} flex flex-col gap-5 p-4 sm:p-6`}>
        <div className="flex flex-wrap items-center gap-4">
          <Avatar name={member.name} size={56} />
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[22px] font-bold leading-tight text-lms-ink sm:text-[26px]">{member.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-lms-sub">
              {member.email && <a href={`mailto:${member.email}`} className="truncate text-lms-link hover:underline">{member.email}</a>}
              {member.membership_code && <span className="font-semibold text-lms-ink lms-num">{member.membership_code}</span>}
            </div>
          </div>
          {stateBadge(s.state, e.status)}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] text-lms-sub">{course.title}</span>
          <ProgressBar pct={s.pct} done={s.done} total={s.total} />
        </div>
      </div>

      <StatCards
        items={[
          { label: t('Progress'), value: `${s.pct}%`, note: `${s.done}/${s.total} ${t('lessons')}`, accent: s.state === 'completed' },
          { label: t('Enrolled'), value: fmtDate(e.enrolled_at), note: e.status === 'pending' ? t('Pending approval') : `${t('Lessons released')}: ${e.released}/${e.total}` },
          { label: t('Last activity'), value: fmtDate(s.last_activity), note: s.current_lesson_title ? `${t('Now on')}: ${s.current_lesson_title}` : undefined },
          { label: t('Evaluations'), value: s.evaluations, note: `${s.evaluations_passed} ${t('passed')}` },
        ]}
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr),360px]">
        {/* ===== CURRICULUM ===== */}
        <section className="flex min-w-0 flex-col gap-3">
          <h2 className={h2}>{t('Lessons')}</h2>
          {d.modules.length === 0 ? (
            <EmptyPanel icon={BookOpen} title={t('This course has no lessons yet.')} />
          ) : (
            <div className={`${adminCard} overflow-hidden`}>
              {d.modules.map((m, i) => <Module key={m.id} m={m} defaultOpen={i === firstOpen} evaluations={d.evaluations} />)}
            </div>
          )}
        </section>

        <div className="flex min-w-0 flex-col gap-6">
          {/* ===== EVALUATIONS ===== */}
          <section className="flex flex-col gap-3">
            <h2 className={h2}>{t('Evaluations')}</h2>
            {d.evaluations.length === 0 ? (
              <p className={`${adminCard} px-5 py-4 text-sm text-lms-sub`}>{t('No evaluation attempts yet.')}</p>
            ) : (
              <ul className={`${adminCard} flex flex-col overflow-hidden`}>
                {d.evaluations.slice(0, 20).map((ev, i) => (
                  <EvalRow key={`${ev.lesson_id}-${ev.taken_at}-${i}`} ev={ev} />
                ))}
              </ul>
            )}
          </section>

          {/* ===== RECENT ACTIVITY ===== */}
          <section className="flex flex-col gap-3">
            <h2 className={h2}>{t('Recent activity')}</h2>
            {d.timeline.length === 0 ? (
              <p className={`${adminCard} px-5 py-4 text-sm text-lms-sub`}>{t('No activity recorded yet.')}</p>
            ) : (
              <ol className={`${adminCard} flex flex-col px-4 py-3`}>
                {d.timeline.map((ev, i) => {
                  const Icon = EVENT[ev.kind] || Clock;
                  return (
                    <li key={`${ev.at}-${i}`} className="relative flex gap-3 pb-4 last:pb-0">
                      {i < d.timeline.length - 1 && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-lms-line" aria-hidden="true" />}
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-lms-raised text-lms-sub">
                        <Icon size={14} />
                      </span>
                      <div className="min-w-0 pt-0.5">
                        <div className="text-[13.5px] text-lms-ink">{eventText(ev)}</div>
                        <div className="text-xs text-lms-faint">{fmtDateTime(ev.at)}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
